
/* 
  Developed by: Jordyn Kao
  Contact: jordyninternational@gmail.com
  This component is part of the application and contains internal credits.
  This code is protected under the terms of the contract agreement.
  Unauthorized use or distribution of this code is prohibited.
*/
import { useState } from 'react';
import { Shield, TrendingUp, ClipboardCheck, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Title } from './Title';

const features = [
  {
    icon: Shield,
    title: 'Access Exclusive Deals',
    description: 'Invest in off-market opportunities, including institutional-grade real estate and private credit, typically reserved for the largest investors.',
    image: 'https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'
  },
  {
    icon: TrendingUp,
    title: 'Build Real Wealth',
    description: 'Earn passive income and build long-term wealth with assets that have the potential for higher returns and lower volatility than public markets.',
    image: 'https://images.pexels.com/photos/164527/pexels-photo-164527.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'
  },
  {
    icon: ClipboardCheck,
    title: 'Invest with Confidence',
    description: 'We handle the sourcing and due diligence, so you can invest in high-quality assets with ease through our simple, secure platform.',
    image: 'https://images.pexels.com/photos/590022/pexels-photo-590022.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'
  }
];

const FeaturesSection = () => {
  const [activeFeature, setActiveFeature] = useState(0);

  return (
    <section className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <Title as="h2" className="text-4xl md:text-5xl">
            A Better Way to Invest
          </Title>
          <p className="mt-4 text-lg text-slate-600 max-w-3xl mx-auto">
            Alta-X opens the door to institutional-quality alternative investments, empowering you to build a stronger, more diversified portfolio.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            {features.map((feature, index) => (
              <div
                key={index}
                onMouseEnter={() => setActiveFeature(index)}
                className={`p-6 rounded-xl cursor-pointer transition-all duration-300 border-2 ${activeFeature === index ? 'bg-white border-[hsl(var(--navy))] shadow-2xl' : 'bg-white border-transparent hover:bg-slate-100/50'}`}>
                <div className="flex items-center space-x-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center transition-colors duration-300 ${activeFeature === index ? 'bg-[hsl(var(--navy))]' : 'bg-slate-100'}`}>
                    <feature.icon className={`w-6 h-6 transition-colors duration-300 ${activeFeature === index ? 'text-white' : 'text-[hsl(var(--navy))]'}`} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{feature.title}</h3>
                </div>
                <p className="mt-4 text-slate-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>

          <div className="relative h-96 w-full rounded-2xl overflow-hidden shadow-2xl">
            <AnimatePresence mode="wait">
              <motion.img
                key={activeFeature}
                src={features[activeFeature].image}
                alt={features[activeFeature].title}
                initial={{ opacity: 0, scale: 1.05 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.5, ease: 'easeInOut' }}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </AnimatePresence>
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
